import { useState, useEffect, useCallback } from 'react';
import {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
  type SignInInput,
  type SignUpInput,
} from 'aws-amplify/auth';
import { clearWidget } from '../lib/widget';

interface AuthUser {
  sub: string;
  email: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const checkSession = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (session.tokens?.accessToken) {
        const sub = session.tokens.accessToken.payload['sub'] as string;
        // ID token contains user attributes (email); access token only has sub/username (UUID)
        const email = (session.tokens.idToken?.payload['email'] as string | undefined)
          ?? (session.tokens.accessToken.payload['username'] as string | undefined)
          ?? '';
        setState({ user: { sub, email }, isAuthenticated: true, isLoading: false });
        return;
      }
    } catch {
      // Not signed in
    }
    // No active session — reflect that on the home-screen widget instead of
    // leaving a stale "today" snapshot that reads as "nothing due".
    void clearWidget();
    setState({ user: null, isAuthenticated: false, isLoading: false });
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  const handleSignIn = async (input: SignInInput) => {
    await signIn(input);
    await checkSession();
  };

  const handleSignUp = async (input: SignUpInput) => {
    return signUp(input);
  };

  const handleConfirmSignUp = async (username: string, code: string) => {
    return confirmSignUp({ username, confirmationCode: code });
  };

  const handleSignOut = async () => {
    await signOut();
    void clearWidget();
    setState({ user: null, isAuthenticated: false, isLoading: false });
  };

  // Step 1 of the forgotten-password flow: emails a confirmation code.
  // Returns Cognito's delivery details (masked destination) for the UI.
  const handleResetPassword = async (username: string) => {
    return resetPassword({ username });
  };

  // Step 2: sets the new password using the emailed code.
  const handleConfirmResetPassword = async (
    username: string,
    confirmationCode: string,
    newPassword: string,
  ) => {
    return confirmResetPassword({ username, confirmationCode, newPassword });
  };

  return {
    ...state,
    signIn: handleSignIn,
    signUp: handleSignUp,
    confirmSignUp: handleConfirmSignUp,
    resetPassword: handleResetPassword,
    confirmResetPassword: handleConfirmResetPassword,
    signOut: handleSignOut,
    refreshSession: checkSession,
  };
}
